import React, { useEffect, useState } from 'react';
import LeftSideMenu from '../components/LeftSideMenu';
import UserNav from '../components/UserNav';
import { Button, Image, Card, Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { config } from '../config/indexConfig';
import Loader from '../components/Loader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link, useNavigate } from 'react-router-dom';
import { useGetProjectAnalyticsQuery } from '../slices/usersApiSlice';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const Dashboard = () => {
    const [selectedProject, setSelectedProject] = useState(null);
    const [userProjects, setUserProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Get user info from localStorage
    const userInfo = useSelector((state) => state.auth.userInfo) || JSON.parse(localStorage.getItem('userInfo') || 'null');

    // Fetch analytics for selected project
    const { data: analyticsData, isLoading: analyticsLoading, error: analyticsError } = useGetProjectAnalyticsQuery(
        selectedProject,
        { skip: !selectedProject }
    );

    useEffect(() => {
        // Fetch user data to get projects
        const fetchUserProjects = async () => {
            try {
                if (!userInfo?.id) {
                    setLoading(false);
                    return;
                }

                const accessToken = localStorage.getItem('accessToken');
                const response = await fetch(`${config.api.baseUrl}/users/${userInfo.id}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                });

                if (response.ok) {
                    const userData = await response.json();
                    const projects = userData.projects || [];
                    setUserProjects(projects);

                    // Auto-select first project if available
                    if (projects.length > 0 && !selectedProject) {
                        setSelectedProject(projects[0].projectName);
                    }
                }
            } catch (error) {
                console.error('Error fetching user projects:', error);
                toast.error('Failed to load projects');
            } finally {
                setLoading(false);
            }
        };

        fetchUserProjects();
    }, [userInfo?.id]);

    // Color palette for charts
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

    // Format data for time series charts
    const formatTimeSeriesData = (data) => {
        if (!data || !Array.isArray(data) || data.length === 0) return [];
        return data.map(item => {
            // Handle date format (can be YYYY-MM-DD string or Date object)
            let dateStr = item.date;
            if (typeof dateStr === 'string' && dateStr.includes('-')) {
                // Parse YYYY-MM-DD format
                const date = new Date(dateStr + 'T00:00:00');
                dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
            return {
                date: dateStr,
                count: item.count || 0
            };
        });
    };

    // Prepare stats cards data
    const statsCards = analyticsData ? [
        { title: 'Total Portfolio Visits', value: analyticsData.totalVisits || 0, icon: '👁️', color: '#0088FE' },
        { title: 'Total Blog Reads', value: analyticsData.totalBlogReads || 0, icon: '📖', color: '#00C49F' },
        { title: 'Total Blogs', value: analyticsData.totalBlogs || 0, icon: '📝', color: '#FFBB28' },
        { title: 'Active Visitors', value: analyticsData.activeVisitors || 0, icon: '👥', color: '#FF8042' },
        { title: 'Resume Downloads', value: analyticsData.totalResumeDownloads || 0, icon: '📥', color: '#8884d8' },
    ] : [];

    if (loading) {
        return <Loader />;
    }

    // Show welcome message if no projects
    if (userProjects.length === 0) {
        return (
            <>
                <div className="pagecontent">
                    <LeftSideMenu />
                    <div className="pagecontentbox">
                        <div className="pagebannerflex">
                            <div className="pagetitle col-lg-6">
                                <h2 className="pagetitleh2">Welcome to Analytics Dashboard!</h2>
                                <p className="content userpagecontent">Create a portfolio to start tracking analytics.</p>
                            </div>
                            <div className="pagebannerimgbg">
                                <Image src={`${process.env.PUBLIC_URL}/images/homepic.png`} className="bannerimg" alt="bannerimg" />
                            </div>
                        </div>
                    </div>
                    <div className="pagecontentbox">
                        <div className="text-center">
                            <h6 className="h6">
                                🎉 Let's get started! Create your{' '}
                                <span>
                                    <Link to="/projects">first portfolio</Link>
                                </span>{' '}
                                to view analytics!
                            </h6>
                            <Image src={`${process.env.PUBLIC_URL}/images/homedash.png`} className="welcomdashimg" alt="dashimg" />
                        </div>
                    </div>
                </div>
                <ToastContainer />
            </>
        );
    }

    return (
        <>
            <div className="pagecontent">
                <LeftSideMenu />
                {!selectedProject ? (
                    <div className="pagecontentbox">
                        <div className="text-center">
                            <Loader />
                        </div>
                    </div>
                ) : analyticsLoading ? (
                    <div className="pagecontentbox">
                        <Loader />
                    </div>
                ) : analyticsError ? (
                    <div className="pagecontentbox">
                        <div className="alert alert-danger">
                            Failed to load analytics. Please try again.
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Stats Cards */}
                        <div className="pagecontentbox">
                            <Row className="g-3">
                                {statsCards.map((stat, index) => (
                                    <Col key={index} xs={12} sm={6} lg={4}>
                                        <Card style={{ borderLeft: `4px solid ${stat.color}` }}>
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <p className="mb-1 text-muted small">{stat.title}</p>
                                                        <h3 className="mb-0" style={{ color: stat.color }}>
                                                            {stat.value}
                                                        </h3>
                                                    </div>
                                                    <div style={{ fontSize: '2.5rem' }}>{stat.icon}</div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </div>

                        {/* Charts */}
                        <div className="pagecontentbox">
                            <Row className="g-3">
                                {/* Portfolio Visits Over Time */}
                                <Col xs={12} lg={6}>
                                    <Card>
                                        <Card.Header>
                                            <h5 className="mb-0">Portfolio Visits Over Time</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <LineChart data={formatTimeSeriesData(analyticsData?.visitsOverTime)}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="date" />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Legend />
                                                    <Line type="monotone" dataKey="count" stroke="#0088FE" strokeWidth={2} />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Blog Reads Over Time */}
                                <Col xs={12} lg={6}>
                                    <Card>
                                        <Card.Header>
                                            <h5 className="mb-0">Blog Reads Over Time</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <LineChart data={formatTimeSeriesData(analyticsData?.blogReadsOverTime)}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="date" />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Legend />
                                                    <Line type="monotone" dataKey="count" stroke="#00C49F" strokeWidth={2} />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Overview Chart */}
                                <Col xs={12}>
                                    <Card>
                                        <Card.Header>
                                            <h5 className="mb-0">Activity Overview</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart
                                                    data={[
                                                        { name: 'Visits', value: analyticsData?.totalVisits || 0 },
                                                        { name: 'Blog Reads', value: analyticsData?.totalBlogReads || 0 },
                                                        { name: 'Downloads', value: analyticsData?.totalResumeDownloads || 0 },
                                                    ]}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="name" />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Bar dataKey="value" fill="#8884d8" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Top Blog Posts */}
                                <Col xs={12}>
                                    <Card>
                                        <Card.Header>
                                            <h5 className="mb-0">Top Blog Posts (by reads)</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            {(!analyticsData?.topBlogs || analyticsData.topBlogs.length === 0) ? (
                                                <div className="text-muted">No blog reads yet.</div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Blog</th>
                                                                <th style={{ width: 120 }}>Reads</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {analyticsData.topBlogs.map((b) => (
                                                                <tr key={b.blogId}>
                                                                    <td>{b.blogId}</td>
                                                                    <td>{b.reads}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </div>
                    </>
                )}
            </div>
            <ToastContainer />
        </>
    );
};

export default Dashboard;
